<template>
  <div class="wth-host">
  <transition name="wth-slide">
    <aside v-if="visible" class="wth" :key="clientId">
      <!-- header -->
      <div class="wth__head">
        <span class="wth__avatar" :style="avatarStyle(name)">{{ initials(name) }}</span>
        <div class="wth__who">
          <span class="wth__name">{{ name }}</span>
          <span class="wth__phone tabular">{{ phone || $t('whatsappHub.table.noPhone') }}</span>
        </div>
        <span class="wth__session" :class="sessionOpen ? 'wth__session--open' : 'wth__session--closed'">
          <i/>{{ sessionOpen ? $t('whatsappHub.table.sessionOpen', {time: timeLeft}) : $t('whatsappHub.table.templateRequired') }}
        </span>
        <button v-if="contractId" type="button" class="wth__tool" v-b-tooltip.hover :title="$t('whatsappHub.actions.reservation')"
                @click="$emit('open-contract', contractId)"><i class="fa-solid fa-file-contract"/></button>
        <button type="button" class="wth__tool" v-b-tooltip.hover :title="$t('whatsappHub.reload')" @click="refresh">
          <i class="fas fa-redo" :class="{'fa-spin': loading}"/>
        </button>
        <button type="button" class="wth__tool" @click="close"><i class="fas fa-times"/></button>
      </div>

      <!-- messages -->
      <div class="wth__body" ref="body">
        <div v-if="loading && messages.length === 0" class="wth__empty"><b-spinner small/></div>
        <div v-else-if="messages.length === 0" class="wth__empty">
          <i class="fab fa-whatsapp"/>
          <p>{{ $t('whatsappHub.thread.noMessages') }}</p>
        </div>
        <template v-else>
          <template v-for="(group, gi) in grouped">
            <div :key="'d' + gi" class="wth__day"><span>{{ dayLabel(group.day) }}</span></div>
            <div v-for="(m, i) in group.messages" :key="gi + '-' + i" class="wth__row" :class="m.isIncoming ? 'is-in' : 'is-out'">
              <div class="wth__bubble" :class="{'wth__bubble--template': m.isTemplate, 'wth__bubble--failed': isFailed(m)}">
                <span v-if="m.isTemplate" class="wth__kind"><i class="fas fa-file-lines"/>{{ m.templateName || $t('whatsappHub.thread.template') }}</span>
                <span v-else-if="m.type === 'BUTTON_REPLY' || m.type === 'LIST_REPLY'" class="wth__kind wth__kind--reply">
                  <i class="fas fa-hand-pointer"/>{{ m.type === 'LIST_REPLY' ? $t('whatsappHub.thread.listReply') : $t('whatsappHub.thread.buttonReply') }}
                </span>
                <a v-if="m.mediaUrl" :href="m.mediaUrl" target="_blank" rel="noopener" class="wth__media">
                  <img v-if="isImage(m)" :src="m.mediaUrl" alt=""/>
                  <span v-else><i class="fas fa-paperclip mr-25"/>{{ $t('whatsappHub.thread.media') }}</span>
                </a>
                <p class="wth__text">{{ strip(m.body) }}</p>
                <span class="wth__meta">
                  <span v-if="m.sentBy && !m.isIncoming" class="wth__by">{{ m.sentBy }}</span>
                  <span class="wth__time tabular">{{ m.date | moment('HH:mm') }}</span>
                  <i v-if="!m.isIncoming" :class="statusMeta(m.status).icon" class="wth__tick" :data-tone="statusMeta(m.status).tone"
                     v-b-tooltip.hover :title="$t('whatsappHub.status.' + statusMeta(m.status).key) + (m.errorMessage ? ' · ' + m.errorMessage : '')"/>
                </span>
                <span v-if="isFailed(m) && (m.errorMessage || m.errorCode)" class="wth__error">
                  <i class="fas fa-triangle-exclamation mr-25"/>{{ m.errorMessage || ('Twilio error ' + m.errorCode) }}
                </span>
              </div>
            </div>
          </template>
        </template>
      </div>

      <!-- composer -->
      <div class="wth__foot">
        <template v-if="sessionOpen">
          <div v-if="showMedia" class="wth__media-row">
            <b-form-input v-model="mediaUrl" size="sm" placeholder="https://…/file.pdf"/>
            <button type="button" class="wth__tool" @click="showMedia = false; mediaUrl = ''"><i class="fas fa-times"/></button>
          </div>
          <div class="wth__compose">
            <button type="button" class="wth__tool" :class="{'wth__tool--on': showMedia}" v-b-tooltip.hover :title="$t('whatsappHub.thread.attachMedia')"
                    :disabled="!canMedia" @click="showMedia = !showMedia"><i class="fas fa-paperclip"/></button>
            <b-form-textarea v-model="draft" rows="1" max-rows="4" :placeholder="$t('whatsappHub.thread.typeMessage')" class="wth__input"
                             @keydown.enter.exact.prevent="send"/>
            <b-button variant="success" class="wth__send" :disabled="sending || (draft.trim() === '' && !mediaUrl)" @click="send">
              <b-spinner v-if="sending" small/><i v-else class="fas fa-paper-plane"/>
            </b-button>
          </div>
        </template>
        <div v-else class="wth__closed">
          <span><i class="fas fa-lock mr-25"/>{{ $t('whatsappHub.thread.sessionClosedHint') }}</span>
          <b-button variant="success" size="sm" :disabled="!phone" @click="$emit('compose-template', recipientRow)">
            <i class="fas fa-file-lines mr-25"/>{{ $t('whatsappHub.actions.sendTemplate') }}
          </b-button>
        </div>
        <button v-if="sessionOpen" type="button" class="wth__tpl-link" @click="$emit('compose-template', recipientRow)">
          <i class="fas fa-file-lines mr-25"/>{{ $t('whatsappHub.thread.sendTemplateInstead') }}
        </button>
      </div>
    </aside>
  </transition>
  </div>
</template>

<script>
import {mapActions} from 'vuex'
import {avatarStyle, initials, statusMeta} from './whatsappUtils'

/**
 * The conversation drawer: the whole chat with delivery ticks, template /
 * button-reply / media markers, and a composer that respects the 24h rule
 * (free text + media while the session is open, template otherwise).
 */
export default {
  name: 'WhatsappThread',
  props: {
    buid: {type: [Number, String], default: null},
    status: {type: Object, default: null},
  },
  data: () => ({
    visible: false,
    clientId: null,
    contractId: null,
    name: '',
    phone: '',
    messages: [],
    sessionOpen: false,
    sessionExpiresAt: null,
    loading: false,
    sending: false,
    draft: '',
    mediaUrl: '',
    showMedia: false,
    poll: null,
    now: null,
  }),
  computed: {
    canMedia() {
      return this.status == null || this.status.canSendMedia !== false;
    },
    grouped() {
      const groups = [];
      this.messages.forEach(m => {
        const day = this.$moment(m.date).format('YYYY-MM-DD');
        let group = groups[groups.length - 1];
        if (!group || group.day !== day) groups.push(group = {day, messages: []});
        group.messages.push(m);
      });
      return groups;
    },
    timeLeft() {
      if (!this.sessionExpiresAt) return '';
      const minutes = this.$moment(this.sessionExpiresAt).diff(this.now || this.$moment(), 'minutes');
      if (minutes <= 0) return '0m';
      const h = Math.floor(minutes / 60), m = minutes % 60;
      return h > 0 ? `${h}h ${m}m` : `${m}m`;
    },
    recipientRow() {
      return {clientId: this.clientId, contractId: this.contractId, name: this.name, phone: this.phone, sessionOpen: this.sessionOpen};
    },
  },
  mounted() {
    // the page content is its own stacking context below the fixed navbar —
    // a fixed drawer only wins by living directly under <body>
    document.body.appendChild(this.$el);
  },
  beforeDestroy() {
    this.stopPolling();
    if (this.$el && this.$el.parentNode === document.body) document.body.removeChild(this.$el);
  },
  methods: {
    ...mapActions('whatsapp', ['hubConversation', 'hubSend']),
    initials, avatarStyle, statusMeta,

    /** @param row anything with clientId (+ name/phone/contractId) */
    open(row) {
      this.clientId = row.clientId;
      this.contractId = row.contractId || null;
      this.name = row.customerName || row.name || [row.firstname, row.lastname].filter(Boolean).join(' ') || ('#' + row.clientId);
      this.phone = row.phone || '';
      this.messages = [];
      this.draft = '';
      this.mediaUrl = '';
      this.showMedia = false;
      this.visible = true;
      this.now = this.$moment();
      this.refresh(true);
      this.startPolling();
    },
    close() {
      this.visible = false;
      this.stopPolling();
      this.$emit('closed', this.clientId);
    },
    startPolling() {
      this.stopPolling();
      this.pollCount = 0;
      this.poll = setInterval(() => {
        this.now = this.$moment();
        // every third poll (30s) also asks Twilio for fresh delivery states
        this.refresh(false, ++this.pollCount % 3 === 0);
      }, 10000);
    },
    stopPolling() {
      if (this.poll) clearInterval(this.poll);
      this.poll = null;
    },
    refresh(markSeen, sync) {
      if (!this.clientId) return;
      const clientId = this.clientId;
      this.loading = true;
      this.hubConversation({clientId, seen: markSeen, sync: sync == null ? !!markSeen : sync}).then(ans => {
        if (clientId !== this.clientId) return;
        const chat = ans.chat;
        const before = this.messages.length;
        this.messages = chat && chat.result ? chat.result.slice() : [];
        // the server resolves the number from the client record (phone, else mobile) — trust it over the row
        if (ans.phone) this.phone = ans.phone;
        else if (chat && chat.phone) this.phone = chat.phone;
        if (ans.name) this.name = ans.name;
        if (!this.contractId && (ans.contractId || (chat && chat.contractId))) this.contractId = ans.contractId || chat.contractId;
        this.sessionOpen = !!ans.sessionOpen;
        this.sessionExpiresAt = ans.sessionExpiresAt;
        if (this.messages.length !== before) this.scrollDown();
        if (markSeen) this.$emit('seen', clientId);
      }).catch(error => {
        this.$store.dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error});
      }).finally(() => this.loading = false);
    },
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.body;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    send() {
      if (this.sending || (this.draft.trim() === '' && !this.mediaUrl)) return;
      this.sending = true;
      const request = {
        buid: this.buid,
        recipients: [{clientId: this.clientId, contractId: this.contractId}],
        mode: this.mediaUrl ? 'MEDIA' : 'TEXT',
        body: this.draft,
        mediaUrl: this.mediaUrl || null,
      };
      this.hubSend(request).then(ans => {
        const result = (ans.results || [])[0];
        if (result && !result.success) return;
        this.draft = '';
        this.mediaUrl = '';
        this.showMedia = false;
        this.refresh(false, false);
        // Twilio moves queued → sent → delivered within seconds; look again shortly after
        setTimeout(() => this.visible && this.refresh(false, true), 4000);
        this.$emit('sent', this.clientId);
      }).catch(() => {
      }).finally(() => this.sending = false);
    },
    strip(text) {
      const div = document.createElement('div');
      div.innerHTML = text || '';
      return div.textContent || div.innerText || '';
    },
    isImage(m) {
      return /image\//.test(m.mediaContentType || '') || /\.(png|jpe?g|gif|webp)(\?.*)?$/i.test(m.mediaUrl || '');
    },
    isFailed(m) {
      return statusMeta(m.status).tone === 'bad';
    },
    dayLabel(day) {
      const m = this.$moment(day);
      if (m.isSame(this.$moment(), 'day')) return this.$t('calendarPicker.today');
      if (m.isSame(this.$moment().subtract(1, 'day'), 'day')) return this.$t('whatsappHub.thread.yesterday');
      return m.format('dddd, DD MMM YYYY');
    },
  }
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }
.wth {
  position: fixed; top: 0; right: 0; bottom: 0; width: 460px; max-width: 100vw; z-index: 1045;
  display: flex; flex-direction: column; background: #fff;
  box-shadow: -12px 0 40px rgba(15, 23, 42, .18); border-left: 1px solid rgba(15, 23, 42, .06);
}
.wth-slide-enter-active, .wth-slide-leave-active { transition: transform .22s ease, opacity .22s ease; }
.wth-slide-enter, .wth-slide-leave-to { transform: translateX(40px); opacity: 0; }

.wth__head { display: flex; align-items: center; gap: 10px; padding: 12px 14px; border-bottom: 1px solid rgba(15, 23, 42, .06); background: #075e54; color: #fff; }
.wth__avatar { width: 38px; height: 38px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: .76rem; font-weight: 800; color: #fff; flex-shrink: 0; }
.wth__who { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.wth__name { font-size: .9rem; font-weight: 800; text-transform: capitalize; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wth__phone { font-size: .7rem; opacity: .8; }
.wth__session {
  display: inline-flex; align-items: center; gap: 6px; font-size: .62rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em;
  padding: 3px 8px; border-radius: 999px; white-space: nowrap;
  i { width: 6px; height: 6px; border-radius: 50%; display: inline-block; }
  &--open { background: rgba(255, 255, 255, .16); i { background: #4ade80; box-shadow: 0 0 0 3px rgba(74, 222, 128, .3); } }
  &--closed { background: rgba(255, 159, 67, .25); i { background: #ffb15c; } }
}
.wth__tool {
  width: 30px; height: 30px; border: none; border-radius: 9px; cursor: pointer; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center; background: rgba(255, 255, 255, .14); color: #fff; font-size: .74rem;
  &:hover { background: rgba(255, 255, 255, .26); }
  &:focus { outline: none; }
  &:disabled { opacity: .4; cursor: not-allowed; }
}
.wth__body {
  flex: 1; overflow-y: auto; padding: 14px 14px 8px;
  background: #efe7dd url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cg fill='%23000' fill-opacity='.035'%3E%3Ccircle cx='10' cy='10' r='2'/%3E%3Ccircle cx='40' cy='30' r='2'/%3E%3Ccircle cx='20' cy='48' r='2'/%3E%3C/g%3E%3C/svg%3E");
}
.wth__empty { text-align: center; padding: 60px 0; color: #94a3b8; i { font-size: 2.4rem; opacity: .35; } p { margin-top: 8px; font-size: .84rem; } }
.wth__day { text-align: center; margin: 8px 0 10px; span { display: inline-block; background: rgba(255, 255, 255, .85); color: #54656f; font-size: .66rem; font-weight: 700; padding: 3px 10px; border-radius: 8px; } }
.wth__row { display: flex; margin-bottom: 6px; &.is-out { justify-content: flex-end; } }
.wth__bubble {
  position: relative; max-width: 82%; background: #fff; border-radius: 10px; padding: 6px 10px 6px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, .08); color: #111b21; font-size: .8rem; line-height: 1.4;
  .is-in & { border-top-left-radius: 2px; }
  .is-out & { border-top-right-radius: 2px; background: #d9fdd3; }
  &--template { border: 1px dashed rgba(37, 211, 102, .6); }
  &--failed { background: #fee2e2 !important; }
}
.wth__kind { display: inline-flex; align-items: center; gap: 5px; font-size: .6rem; font-weight: 800; text-transform: uppercase; letter-spacing: .05em; color: #1f9d57; margin-bottom: 2px; &--reply { color: #7367f0; } }
.wth__media { display: block; margin-bottom: 4px; color: #00a5f4; font-size: .74rem; font-weight: 700; img { max-width: 100%; border-radius: 8px; display: block; } }
.wth__text { margin: 0; white-space: pre-wrap; word-break: break-word; }
.wth__meta { display: flex; justify-content: flex-end; align-items: center; gap: 5px; margin-top: 2px; font-size: .6rem; color: #667781; }
.wth__by { font-style: italic; }
.wth__tick { font-size: .66rem; &[data-tone="ok"] { color: #8696a0; } &[data-tone="read"] { color: #34b7f1; } &[data-tone="bad"] { color: #ea5455; } &[data-tone="muted"] { color: #b9c3ca; } }
.wth__error { display: block; margin-top: 4px; font-size: .66rem; color: #b91c1c; font-weight: 700; }

.wth__foot { padding: 10px 12px; border-top: 1px solid rgba(15, 23, 42, .06); background: #f0f2f5; display: flex; flex-direction: column; gap: 6px; }
.wth__media-row { display: flex; gap: 6px; align-items: center; .wth__tool { background: rgba(15, 23, 42, .08); color: #64748b; } }
.wth__compose { display: flex; align-items: flex-end; gap: 6px; .wth__tool { background: rgba(15, 23, 42, .08); color: #64748b; &--on { background: rgba(37, 211, 102, .2); color: #1f9d57; } } }
.wth__input { border-radius: 18px; resize: none; }
.wth__send { width: 40px; height: 38px; border-radius: 50% !important; padding: 0 !important; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.wth__closed {
  display: flex; align-items: center; justify-content: space-between; gap: 10px; font-size: .74rem; color: #c77400;
  .btn { white-space: nowrap; flex-shrink: 0; }
}
.wth__tpl-link { border: none; background: none; align-self: flex-end; font-size: .68rem; font-weight: 700; color: #64748b; cursor: pointer; padding: 0; &:hover { color: #1f9d57; } &:focus { outline: none; } }

@media (max-width: 576px) { .wth { width: 100vw; } }
</style>

<style lang="scss">
.dark-layout {
  .wth { background: #283046; border-color: rgba(255, 255, 255, .08); }
  .wth__body { background-color: #161d31; }
  .wth__day span { background: rgba(255, 255, 255, .1); color: #b4b7bd; }
  .wth__bubble { background: #283046; color: #e9ecf2; }
  .wth__row.is-out .wth__bubble { background: #0f4b3f; }
  .wth__bubble--failed { background: #4a1d1d !important; }
  .wth__meta { color: #94a3b8; }
  .wth__foot { background: #283046; border-color: rgba(255, 255, 255, .08); }
  .wth__compose .wth__tool, .wth__media-row .wth__tool { background: rgba(255, 255, 255, .08); color: #94a3b8; }
  .wth__tpl-link { color: #94a3b8; }
}
</style>
